import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Headphones, Instagram, Mic, Users, HeartHandshake, Baby } from "lucide-react";
import RevealText from "../components/RevealText";
import { BRAND } from "../lib/brand";

const SPOTIFY_URL = "https://open.spotify.com/show/033uq0VkPLz3ITlATAEqnD?si=a1f0b9d7164a48e6";
const SPOTIFY_EMBED = "https://open.spotify.com/embed/show/033uq0VkPLz3ITlATAEqnD?utm_source=generator&theme=0";
const LATEST_EPISODE_ID = "450wzcdkyxhTZMW1SXl3eK";
const LATEST_EPISODE_EMBED = `https://open.spotify.com/embed/episode/${LATEST_EPISODE_ID}?utm_source=generator&theme=0`;
const LATEST_EPISODE_URL = `https://open.spotify.com/episode/${LATEST_EPISODE_ID}`;
const INSTAGRAM_URL = "https://www.instagram.com/vilardo_teresa.infantcare?igsh=d3B2a2U4ZTljdzZ4";

const TOPICS = [
  "Cura educativa a domicilio",
  "Lavoro della tata professionista",
  "Primi anni di vita",
  "Bisogni dei bambini 0–36 mesi",
  "Relazione tata, bambino e genitori",
  "Alleanza educativa",
  "Osservazione e tempi individuali",
  "Sonno, routine, accompagnamento",
  "Formazione e identità professionale",
  "Solitudine e responsabilità del lavoro nelle case",
  "Incontri che lasciano un segno",
];

const AUDIENCE = [
  {
    icon: HeartHandshake,
    tag: "Per i genitori",
    body: "Per ritrovare comprensione, sostegno e nuovi punti di vista sulle esperienze quotidiane vissute con i propri figli.",
  },
  {
    icon: Baby,
    tag: "Per le tate professioniste",
    body: "Per sentirsi meno sole, riflettere sul proprio ruolo e riconoscere il valore educativo del lavoro nelle famiglie.",
  },
  {
    icon: Users,
    tag: "Per educatrici ed educatori",
    body: "Per osservare l'infanzia dal punto di vista della casa, delle relazioni familiari e della continuità educativa.",
  },
];

const EPISODES = [
  { n: "01", title: "Da dove comincia il mio viaggio", desc: "La mia storia, le prime esperienze con i bambini e la nascita della mia identità professionale." },
  { n: "02", title: "Dall'istinto alla formazione", desc: "Il momento in cui ho compreso che l'esperienza da sola non basta: il lavoro educativo richiede studio, confronto e formazione continua." },
  { n: "03", title: "Matteo e Anita: i primi passi professionali", desc: "Due incontri importanti, i primi contratti e la consapevolezza che lavorare con i bambini sarebbe diventato molto più di un impiego." },
  { n: "04", title: "Quando nasce una rete tra tate", desc: "La nascita del gruppo dedicato alle tate, il bisogno di confronto e il valore della collaborazione tra professioniste." },
  { n: "05", title: "Il valore dello stare", desc: "Una riflessione sulla presenza educativa, sull'ascolto e sulla capacità di accompagnare senza invadere." },
  { n: "06", title: "Dadadin: quando un bambino ti sceglie.", desc: "La storia di Leodin, di un nome inventato e della fiducia che nasce quando un bambino ti lascia entrare davvero nel suo mondo ❤️" },
  { n: "07", title: "Dietro la porta di una famiglia", desc: "Cosa significa entrare ogni giorno nella parte più intima della vita di una famiglia? Racconto il privilegio, la responsabilità e la solitudine del lavoro educativo a domicilio." },
  { n: "08", title: "È questione di fiducia: costruendo una alleanza educativa", desc: "La fiducia tra famiglia e professionista non nasce in un giorno: si costruisce con ascolto, rispetto e confronto. Racconto come può nascere una vera alleanza educativa, capace di sostenere il bambino senza giudicare né sostituire i genitori." },
  { n: "09", title: "Giocare è una cosa seria.", desc: "Il gioco non è un passatempo: è il modo in cui il bambino conosce sé stesso, il mondo e le proprie possibilità. Un episodio per ricordarci — genitori, tate ed educatori — che quando un bambino gioca sta compiendo il lavoro più importante della sua vita." },
];

export default function Podcast() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bearY = useTransform(scrollYProgress, [0, 1], ["0%", "70%"]);
  const bearRotate = useTransform(scrollYProgress, [0, 1], [0, 30]);

  return (
    <div data-testid="podcast-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[85vh] pt-32 md:pt-40 pb-16 px-6 md:px-10 overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Il mio podcast</p>
          <h1 className="font-display text-[14vw] md:text-[10vw] lg:text-[12rem] leading-[0.82] text-[#3D2140]">
            <RevealText text={["Una casa", <span key="1" className="text-[#FF2768]">alla volta.</span>]} stagger={0.14} />
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8">
            <p className="md:col-span-7 md:col-start-4 font-editorial text-2xl md:text-3xl text-[#3D2140] leading-snug italic">
              Un bambino alla volta. Una famiglia alla volta.<br/>
              Il podcast di Teresa Vilardo — educatrice per l'infanzia e tata professionista.
            </p>
          </div>
          <div className="mt-10 md:ml-[25%]">
            <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-spotify-cta" className="btn-tv-primary">
              <Headphones size={16} /> Ascolta su Spotify
            </a>
          </div>
        </div>

        <motion.img
          src={BRAND.logo}
          alt=""
          aria-hidden
          style={{ y: bearY, rotate: bearRotate }}
          className="absolute right-4 md:right-16 top-24 md:top-40 h-32 md:h-56 w-32 md:w-56 rounded-full opacity-80"
        />
      </section>

      {/* ULTIMA PUNTATA - highlight */}
      <section className="px-6 md:px-10 pt-8 md:pt-12" data-testid="latest-episode-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FF2768] text-white text-[10px] uppercase tracking-[0.3em] font-semibold">
              <Mic size={12} /> Ultima puntata
            </span>
            <p className="text-sm text-[#3D2140]/70 font-editorial italic">Ascoltala ora</p>
          </div>
          <div className="rounded-[24px] overflow-hidden bg-white border-2 border-[#FF2768]/30 shadow-xl">
            <iframe
              src={LATEST_EPISODE_EMBED}
              title="Ultima puntata — Una casa alla volta"
              width="100%"
              height="232"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              data-testid="latest-episode-player"
              style={{ display: "block" }}
            />
          </div>
          <div className="mt-4 flex justify-end">
            <a href={LATEST_EPISODE_URL} target="_blank" rel="noopener noreferrer" data-testid="latest-episode-cta" className="text-sm text-[#3D2140] link-underline inline-flex items-center gap-2">
              Apri su Spotify <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* SPOTIFY EMBED PLAYER */}
      <section className="px-6 md:px-10 py-12 md:py-16 max-w-[1400px] mx-auto" data-testid="spotify-embed-section">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#3D2140]/60 mb-4">— Tutte le puntate</p>
        <div className="rounded-[24px] overflow-hidden bg-white border border-[#81C3C9]/40 shadow-lg">
          <iframe
            src={SPOTIFY_EMBED}
            title="Una casa alla volta — Podcast"
            width="100%"
            height="352"
            frameBorder="0"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            loading="lazy"
            data-testid="spotify-player"
            style={{ display: "block" }}
          />
        </div>
      </section>

      {/* INTRO */}
      <section className="px-6 md:px-10 py-20 md:py-28 bg-[#81C3C9]/25" data-testid="intro-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Perché "Una casa alla volta"</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              Un diario<br/><span className="text-[#FF2768]">professionale<br/>e personale.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 space-y-5 text-lg text-[#3D2140]/85 leading-relaxed">
            <p>Un podcast dedicato all'infanzia, alle famiglie e al lavoro educativo vissuto ogni giorno dentro le case.</p>
            <p>"Una casa alla volta" nasce dal desiderio di raccontare ciò che accade quando si entra nella quotidianità di una famiglia con <strong>rispetto, competenza e disponibilità all'ascolto</strong>.</p>
            <p>Non è un podcast di lezioni perfette o soluzioni valide per tutti. È un diario fatto di incontri, esperienze, domande, legami e riflessioni educative.</p>
            <p className="font-editorial text-2xl text-[#3D2140] italic pt-2">
              Ogni casa ha i suoi ritmi.<br/>Ogni bambino ha il proprio tempo.<br/>Ogni famiglia ha una storia.
            </p>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="topics-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-6">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Di cosa parlo</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Il viaggio<br/>nel mondo<br/><span className="text-[#FF2768]">dell'infanzia.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:col-start-8 self-end">
            <p className="text-lg text-[#3D2140]/75 leading-relaxed font-editorial italic">
              Ogni episodio unisce il racconto di un'esperienza realmente vissuta a una riflessione educativa e pedagogica. Le storie sono sempre trattate nel rispetto della privacy.
            </p>
          </div>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
          {TOPICS.map((t, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, x: -10 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
              className="flex items-baseline gap-4 border-b border-[#3D2140]/15 py-4 group"
            >
              <span className="font-editorial text-[#FF2768] text-sm shrink-0 w-6">0{i + 1}</span>
              <span className="text-lg md:text-xl text-[#3D2140] group-hover:text-[#FF2768] transition-colors">{t}</span>
            </motion.li>
          ))}
        </ul>
      </section>

      {/* AUDIENCE */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#FAF8F5]" data-testid="audience-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="mb-16 max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Per chi</p>
            <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
              Per chi si prende<br/><span className="text-[#FF2768]">cura ogni giorno.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {AUDIENCE.map((a, i) => {
              const Icon = a.icon;
              return (
                <motion.div
                  key={a.tag}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className={`p-8 rounded-3xl bg-white border border-[#81C3C9]/40 hover:border-[#FF2768] transition-colors duration-500 ${i === 1 ? "md:mt-8" : ""}`}
                  data-testid={`audience-${i}`}
                >
                  <span className="h-14 w-14 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center mb-6">
                    <Icon size={22} />
                  </span>
                  <p className="font-display text-3xl md:text-4xl text-[#3D2140] mb-4">{a.tag}</p>
                  <p className="text-[#3D2140]/75 leading-relaxed">{a.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STARE */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#3D2140] text-white relative overflow-hidden" data-testid="stare-section">
        <img src={BRAND.logo} alt="" aria-hidden className="absolute -right-24 -bottom-24 h-96 w-96 opacity-15" />
        <div className="max-w-[1400px] mx-auto relative">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FFE45C] mb-6">— Il cuore</p>
          <h2 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] mb-10">
            Non solo fare,<br/><span className="text-[#FF2768]">ma stare.</span>
          </h2>
          <div className="max-w-2xl space-y-5 text-lg md:text-xl leading-relaxed text-white/85 font-editorial italic">
            <p>Stare accanto a un bambino senza anticiparlo.</p>
            <p>Stare nella fatica di un genitore senza giudicarlo.</p>
            <p>Stare in una casa sapendo quando intervenire, quando proporre, quando ascoltare e quando diventare quasi invisibili.</p>
            <p className="text-[#81C3C9] pt-4 not-italic font-sans text-base leading-relaxed">
              Per me educare significa questo: offrire una presenza stabile, rispettosa e competente, capace di adattarsi senza perdere la propria identità professionale.
            </p>
          </div>
        </div>
      </section>

      {/* EPISODES */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1600px] mx-auto" data-testid="episodes-section">
        <div className="mb-16">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-4">— Gli episodi</p>
          <h2 className="font-display text-6xl md:text-8xl text-[#3D2140] leading-[0.9]">
            Ascolta il<br/><span className="text-[#FF2768]">racconto.</span>
          </h2>
        </div>

        <div className="space-y-4 md:space-y-5">
          {EPISODES.map((ep, i) => (
            <motion.a
              key={ep.n}
              href={SPOTIFY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`episode-${ep.n}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group flex flex-col md:flex-row md:items-center gap-4 md:gap-8 p-6 md:p-8 rounded-[28px] bg-white border border-[#81C3C9]/30 hover:border-[#FF2768] transition-colors duration-500"
            >
              <span className="font-display text-6xl md:text-7xl text-[#FF2768] leading-none shrink-0 w-24">EP {ep.n}</span>
              <div className="flex-1">
                <h3 className="font-display text-3xl md:text-4xl text-[#3D2140] leading-tight mb-2 group-hover:text-[#FF2768] transition-colors">{ep.title}</h3>
                <p className="text-[#3D2140]/70 leading-relaxed">{ep.desc}</p>
              </div>
              <span className="h-12 w-12 rounded-full bg-[#FF2768]/10 group-hover:bg-[#FF2768] text-[#FF2768] group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
                <Headphones size={18} />
              </span>
            </motion.a>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" data-testid="episodes-spotify-cta" className="btn-tv-primary">
            Scopri tutti gli episodi <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* CHI SONO */}
      <section className="px-6 md:px-10 py-24 md:py-32 bg-[#81C3C9]/25" data-testid="chi-sono-section">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <img
              src={BRAND.photos.casualWindow}
              alt="Teresa Vilardo"
              className="w-full aspect-[4/5] object-cover rounded-[40px] border-4 border-white shadow-2xl"
            />
          </div>
          <div className="md:col-span-7 md:col-start-6 space-y-5 text-lg text-[#3D2140]/85 leading-relaxed">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768]">— Chi sono</p>
            <h2 className="font-display text-5xl md:text-7xl text-[#3D2140] leading-[0.9]">
              23 anni accanto ai<br/><span className="text-[#FF2768]">bambini.</span>
            </h2>
            <p>Sono Teresa Vilardo — educatrice per l'infanzia, tata professionista, Family Coach Montessori, educatrice neonatale, consulente per genitori e formatrice per tate ed educatori.</p>
            <p>Da oltre ventitré anni lavoro con i bambini e accompagno le famiglie direttamente nelle loro case, con particolare attenzione alla fascia da 0 a 36 mesi. Mi occupo di cura educativa a domicilio, sonno infantile, routine, svezzamento, abbandono rispettoso del ciuccio e del pannolino, sviluppo psicomotorio, massaggio infantile e sostegno alla genitorialità.</p>
            <p className="font-editorial text-2xl text-[#3D2140] italic pt-2">
              Il podcast nasce dall'incontro tra il mio lavoro, la mia formazione e tutte le storie che hanno attraversato il mio cammino.
            </p>
            <div className="pt-4">
              <Link to="/chi-sono" data-testid="chi-sono-cta" className="btn-tv-outline">
                Scopri di più su di me <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-6 md:px-10 py-24 md:py-32 max-w-[1400px] mx-auto text-center" data-testid="final-cta">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] mb-6">— Ascolta ora</p>
        <h2 className="font-display text-6xl md:text-8xl lg:text-9xl leading-[0.85] text-[#3D2140] mb-8">
          Una casa<br/>alla <span className="text-[#FF2768]">volta.</span>
        </h2>
        <p className="max-w-2xl mx-auto text-[#3D2140]/75 text-lg leading-relaxed mb-10 font-editorial italic">
          Indossa le cuffie, scegli un episodio e entra insieme a me nelle case, nelle relazioni e nei piccoli grandi momenti che costruiscono l'infanzia.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href={SPOTIFY_URL} target="_blank" rel="noopener noreferrer" data-testid="final-spotify-cta" className="btn-tv-primary">
            <Headphones size={16} /> Ascolta su Spotify
          </a>
          <Link to="/prenota" data-testid="final-contact-cta" className="btn-tv-outline">
            Contattami <ArrowRight size={16} />
          </Link>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="final-instagram-cta" className="inline-flex items-center gap-2 px-7 py-[14px] rounded-full border border-[#3D2140]/25 text-[#3D2140] hover:border-[#FF2768] hover:text-[#FF2768] transition-all">
            <Instagram size={16} /> Seguimi su Instagram
          </a>
        </div>
      </section>
    </div>
  );
}
